// @ts-strict-ignore
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';

import { PersonalRoutine, PersonalRoutineType } from 'rainbow-web-sdk/models/personalRoutine.model';

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { ContactService } from '../core/contact.service';

export const PERSONAL_ROUTINES_SVC = 'PersonalRoutinesService';

/**
 * Collection of events fired by {@link PersonalRoutinesService}
 * @public
 * @readonly
 */
export enum PersonalRoutineEvent {

    /**
     * This event is fired when the personal routine list is updated (at start-up for example)
     * @public
     */
    RAINBOW_ONPERSONALROUTINELISTUPDATED = "onpersonalroutinelistupdated",
    /**
     * This event is fired when a personal routine is being activated
     * @public
     */
    RAINBOW_ONACTIVATINGPERSONALROUTINE = "onactivatingpersonalroutine",
    /**
     * This event is fired when a personal routine is activated
     * @public
     * @param routineId - ID of routine that has been activated
     */
    RAINBOW_ONPERSONALROUTINEACTIVATED = "onpersonalroutineactivated",

    /**
     * This event is fired when a personal routine is updated
     * @public
     * @param routineId - ID of routine that has been updated
     */
    RAINBOW_ONPERSONALROUTINEUPDATED = "onpersonalroutineupdated",

    /**
     * This event is fired when a personal routine is created
     * @public
     * @param routineId - ID of routine that has been created
     */
    RAINBOW_ONPERSONALROUTINECREATED = "onpersonalroutinecreated",

    /**
     * This event is fired when a personal routine is deleted
     * @public
     * @param routineId - ID of routine that has been deleted
     */
    RAINBOW_ONPERSONALROUTINEDELETED = "onpersonalroutinedeleted"
}

/**
 * This service manages personal routines
 * @public
 */
export class PersonalRoutinesService extends Service {

    /*********************************************************/
    /** Services used                                       */
    /*********************************************************/
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private i18nService: I18nService = I18nService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private contactService: ContactService = ContactService.getInstance();

    private portalURL: string = config.restServerUrl + '/api/rainbow/voice/v1.0/personalroutines';
    private listeners = [];
    private xmppMessageHandler: any = null;
    private _personalRoutines: PersonalRoutine[] = [];
    public get personalRoutines(): PersonalRoutine[] {
        return this._personalRoutines;
    }

    public set personalRoutines(value: PersonalRoutine[]) {
        // todo cch: protect setter and throw error when trying to modify ?
        this._personalRoutines = value;
    }

    private _activatingRoutine: boolean = false;
    public get activatingRoutine(): boolean {
        return this._activatingRoutine;
    }

    public set activatingRoutine(value: boolean) {
        this._activatingRoutine = value;
        if (value) { this.sendEvent(PersonalRoutineEvent.RAINBOW_ONACTIVATINGPERSONALROUTINE, null); }
    }

    private rxSubject: Subject<any> = new Subject<any>();

    public static getInstance() {
        return Service.create(PERSONAL_ROUTINES_SVC, PersonalRoutinesService);
    }

    public static build() {
        return new PersonalRoutinesService();
    }

    private constructor() { super(); }

    /************************************************************/
    /** OBSERVABLE STUFF                                       */
    /**/
    public subscribe(callback: any): Subscription { return this.rxSubject.subscribe(callback); }
    public sendEvent(type: PersonalRoutineEvent, value: any) { this.rxSubject.next({ type: type, value: value }); }

    public async start() {
        try {
            this.logger.info('');
            this.logger.info('[PersonalRoutinesService] === STARTING ===');
            this.attachHandlers();

            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.CLOUD_PBX_PERSONAL_ROUTINES) && this.contactService.userContact?.isRainbowHubUser()) {
                await this.retrieveAllPersonalRoutinesFromServer(true);
            }
            this.logger.info('[PersonalRoutinesService] === STARTED ===');

        }
        catch (error) {
            this.logger.error('[PersonalRoutinesService] === STARTING FAILURE === ' + error.message);
        }
    }

    public stop() {

        this.logger.info('');
        this.logger.info('[PersonalRoutinesService] === STOPPING ===');

        // Destroy listeners
        this.listeners.forEach((listener) => { listener(); });
        this.personalRoutines = [];

        this.logger.info('[PersonalRoutinesService] === STOPPED ===');
    }

    public async reconnect(attempt: number = 0) {
        const personalRoutineEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.CLOUD_PBX_PERSONAL_ROUTINES);
        const isHubUser: boolean = this.contactService.userContact?.isRainbowHubUser();

        if (!personalRoutineEnabled || !isHubUser) { this.logger.info(`[PersonalRoutinesService] === Reconnect skipped `); return; }
        try {
            this.logger.info('[PersonalRoutinesService] reconnect');
            attempt++;
            this.attachHandlers();

            await this.retrieveAllPersonalRoutinesFromServer(true);
            this.logger.info('[PersonalRoutinesService] reconnect success');
        }
        catch (error) {
            this.logger.error('[PersonalRoutinesService] reconnect failure -- ' + error.message);
            if (attempt === 1) { this.reconnect(attempt); }
        }
    }

    public attachHandlers() {
        this.logger.info('[PersonalRoutinesService] attachHandlers');
        if (this.xmppMessageHandler) { this.xmppService.connection.deleteHandler(this.xmppMessageHandler); this.xmppMessageHandler = null; }
        this.xmppMessageHandler = this.xmppService.connection.addHandler((stanza: any) => { return this.onHeadlineMessageReceived(stanza); }, 'urn:xmpp:pbxagent:telephony:1', "message", "headline");
    }

    private onHeadlineMessageReceived(stanza: any): boolean {
        try {
            const stanzaElem = $(stanza);
            const personalRoutinesElem = stanzaElem.find("personalRoutines");
            const personalRoutinesModificationElem = personalRoutinesElem.find('personalRoutinesModification');

            if (personalRoutinesElem?.length && personalRoutinesModificationElem && personalRoutinesModificationElem.length) {
                const routineId = personalRoutinesModificationElem.attr('routineId');
                const action = personalRoutinesModificationElem.attr('action');

                // Check whether the event concerns me
                const exists = this.personalRoutines.some(routine => routine.id === routineId);
                if (!exists && action !== 'create') {
                    this.logger.debug(`[PersonalRoutinesService] onHeadlineMessageReceived -- headline personalRoutines event - ${routineId} - action: ${action} - unknown routine`);
                    return true;
                }

                this.logger.debug(`[PersonalRoutinesService] onHeadlineMessageReceived -- headline personalRoutines event - ${routineId} - action: ${action}`);

                switch (action) {
                    case 'activate':
                        this.onPersonalRoutineActivation(routineId);
                        break;
                    case 'update':
                        this.onPersonalRoutineUpdate(routineId);
                        break;
                    case 'create':
                        this.onPersonalRoutineCreated(routineId);
                        break;
                    case 'delete':
                        this.onPersonalRoutineDeleted(routineId);
                        break;
                    default:
                        break;
                }

            }
            return true;
        }
        catch (err) {
            this.logger.error("[PersonalRoutinesService] onHeadlineMessageReceived -- failure -- " + err.message);
            return true;
        }
    }

    private onPersonalRoutineActivation(routineId: string) {
        this.personalRoutines.forEach((routine) => {
            if (routine.id === routineId) {
                this.logger.info(`[PersonalRoutinesService] Routine "${routine.name}" of type "${routine.type}" activated`);
            }
            routine.active = routine.id === routineId;
        });
        this.activatingRoutine = false;
        this.sendEvent(PersonalRoutineEvent.RAINBOW_ONPERSONALROUTINEACTIVATED, routineId);
    }

    private async onPersonalRoutineUpdate(routineId: string) {
        try {
            await this.retrievePersonalRoutineFromServer(routineId, true);
            this.sendEvent(PersonalRoutineEvent.RAINBOW_ONPERSONALROUTINEUPDATED, routineId);
        } catch {
            this.logger.error(`[PersonalRoutinesService] Error retrieving update for routine ${routineId}`);
        }
    }

    private async onPersonalRoutineCreated(routineId: string) {
        try {
            const existingRoutine = this.personalRoutines.some((personalRoutine) => {
                return personalRoutine.id === routineId;
            });
            if (!existingRoutine) {
                await this.retrievePersonalRoutineFromServer(routineId, true);
            }
            this.sendEvent(PersonalRoutineEvent.RAINBOW_ONPERSONALROUTINECREATED, routineId);
        } catch {
            this.logger.error(`[PersonalRoutinesService] Error creating routine ${routineId}`);
        }
    }

    private onPersonalRoutineDeleted(routineId: string) {
        try {
            const existingRoutineIndex = this.personalRoutines.findIndex((personalRoutine) => {
                return personalRoutine.id === routineId;
            });
            if (existingRoutineIndex) {
                this.personalRoutines.splice(existingRoutineIndex, 1);
            }
            this.sendEvent(PersonalRoutineEvent.RAINBOW_ONPERSONALROUTINEDELETED, routineId);
        } catch {
            this.logger.error(`[PersonalRoutinesService] Error creating routine ${routineId}`);
        }
    }

    /**
     * Get the personal routine currently active
     * @public
     * @returns active personal routine object
     */
    public getActiveRoutine(): PersonalRoutine {
        const activeRoutineIndex = this.personalRoutines.findIndex((routine) => {
            return routine.active;
        });
        return this.personalRoutines[activeRoutineIndex];
    }

    /**
     * Get the first personal routine that is not the default work routine
     * @public
     * @returns first personal routine that is not the default work routine
     */
    public getFirstNoWorkRoutine(): PersonalRoutine {
        const noWorkRoutineIndex = this.personalRoutines.findIndex((routine) => {
            return routine.type !== PersonalRoutineType.PERSONALROUTINE_ATWORK;
        });
        return this.personalRoutines[noWorkRoutineIndex];
    }

    /**
     * Get personal routines of user, except the default work routine
     * @public
     * @returns array of personal routines
     */
    public getPersonalRoutinesExceptWork(): PersonalRoutine[] {

        const atWorkRoutineIndex = this.personalRoutines.findIndex((routine) => {
            return routine.type === PersonalRoutineType.PERSONALROUTINE_ATWORK;
        });
        const allRoutines = [...this.personalRoutines];
        allRoutines.splice(atWorkRoutineIndex, 1); // remove work routine
        return allRoutines;
    }

    public getRoutineById(routineId: string) {
        return this.personalRoutines.find((routine) => { return routine.id === routineId; });
    }

    /**
     * Activate a personal routine
     * @public
     * @param routineId - id of routine to activate
     * @param userId - id of the user for which the routine is activated (used by supervisors only). If not set, the user modified is the logged user
     * @returns resolves with the routine data from server
     */
    public async activatePersonalRoutine(routineId: string, userId: string = ""): Promise<any> {
        try {
            if (!routineId) { throw new Error(`[PersonalRoutinesService] activatePersonalRoutine -- no routineID for activation `); }

            const url = new URL(`${this.portalURL}/${routineId}/activate`);
            if (userId) { url.searchParams.set("userId", userId); }

            if (userId === "") { this.activatingRoutine = true; }
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            const routinesData = (await response.json()).data;

            this.logger.debug(`[PersonalRoutinesService] activatePersonalRoutine -- SUCCESS -- `);

            return routinesData;

        } catch (error) {
            this.activatingRoutine = false;
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'activatePersonalRoutine');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Retrieve a personal routine from server by its ID
     * @public
     * @param routineId - id of routine to retrieve
     * @param updateLocalData - whether we should update local personal routine data. Default value: false
     * @returns resolves with the personal routine
     */
    public async retrievePersonalRoutineFromServer(routineId: string, updateLocalData: boolean = false): Promise<PersonalRoutine> {
        try {
            const url = `${this.portalURL}/${routineId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const personalRoutineData = (await response.json()).data;

            if (updateLocalData) {
                if (personalRoutineData?.name) {
                    if (personalRoutineData.type === PersonalRoutineType.PERSONALROUTINE_CUSTOM) {
                        personalRoutineData.displayName = personalRoutineData.name;
                    } else {
                        personalRoutineData.displayName = this.i18nService.translate(personalRoutineData.name) ? this.i18nService.translate(personalRoutineData.name.replace(/ /g, "_")) : this.i18nService.translate("At_work");
                    }
                }
                const personalRoutineIndex = this.personalRoutines.findIndex((personalRoutine: PersonalRoutine) => { return personalRoutine.id === personalRoutineData.id; });
                if (personalRoutineIndex > -1) {
                    this.personalRoutines[personalRoutineIndex].updateRoutineFromData(personalRoutineData);
                    this.logger.info(`[PersonalRoutinesService] retrievePersonalRoutineFromServer - updated:\n${JSON.stringify(this.personalRoutines[personalRoutineIndex])}`);
                }
                else {
                    const newRoutine = PersonalRoutine.createRoutineFromData(personalRoutineData);
                    this.personalRoutines.push(newRoutine);
                    this.logger.info(`[PersonalRoutinesService] retrievePersonalRoutineFromServer - Created:\n${JSON.stringify(newRoutine)}`);

                }
            }
            this.logger.debug(`[PersonalRoutinesService] retrievePersonalRoutineFromServer -- SUCCESS -- `);

            return personalRoutineData;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalRoutine');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Retrieve all personal routines from server
     * @public
     * @param updateLocalData - whether we should update local personal routine data. Default value: false
     * @returns resolves with the personal routines data from server
     */
    public async retrieveAllPersonalRoutinesFromServer(updateLocalData: boolean = false): Promise<any> {
        try {
            this.logger.info(`[PersonalRoutinesService] retrieveAllPersonalRoutinesFromServer`);
            const url = this.portalURL;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const personalRoutinesData = (await response.json()).data;

            if (updateLocalData && personalRoutinesData.routines && personalRoutinesData.routines.length) {
                this.personalRoutines = [];
                const promiseChain = [];

                personalRoutinesData.routines.forEach((routineInfo) => {
                    if (routineInfo.id) {
                        //keep the order of the routines send by the server
                        this.personalRoutines.push(PersonalRoutine.createRoutineFromData({ id: routineInfo.id }));
                        promiseChain.push(this.retrievePersonalRoutineFromServer(routineInfo.id, true));
                    }
                });
                await Promise.all(promiseChain);

            }

            this.sendEvent(PersonalRoutineEvent.RAINBOW_ONPERSONALROUTINELISTUPDATED, this.personalRoutines);
            return personalRoutinesData;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAllPersonalRoutines');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Retrieve all personal routines from server for a given contact
     * @public
     * @param userId - Identifier of the user for which we want to get the personal routines.
     * @returns resolves with the personal routines data from server
     */
    public async retrieveAllPersonalRoutinesForContactFromServer(userId: string): Promise<any> {
        try {
            this.logger.info(`[PersonalRoutinesService] retrieveAllPersonalRoutinesForContactFromServer for ${userId}`);

            const url = `${this.portalURL}?userId=${userId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const personalRoutinesData = (await response.json()).data;
            if (personalRoutinesData?.routines)
                return personalRoutinesData.routines;
            return [];

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveAllPersonalRoutinesForContactFromServer');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Updates a personal routine data
     * @public
     * @param routineId - id of routine to update
     * @param data - data for update
     * @returns resolves with the updated personal routine data from server
     */
    public async updatePersonalRoutine(routineId: string, data: any): Promise<any> {
        try {
            if (!routineId) { throw new Error(`[PersonalRoutinesService] updatePersonalRoutine -- no routineID for update `); }

            const url = `${this.portalURL}/${routineId}`;
            const headers = this.authService.getPostHeader();

            if (data?.immediateCallForward && data.immediateCallForward.destinationType === "doNotForward") {
                data.immediateCallForward.destinationType = null;
                data.immediateCallForward.activate = false;
            } else if (data?.busyCallForward && data.busyCallForward.destinationType === "doNotForward") {
                data.busyCallForward.destinationType = null;
                data.busyCallForward.activate = false;
            } else if (data?.noreplyCallForward && data.noreplyCallForward.destinationType === "doNotForward") {
                data.noreplyCallForward.destinationType = null;
                data.noreplyCallForward.activate = false;
            } else if (data?.cliOptions) {
                if (data.cliOptions.phoneNumberId === "") { delete data.cliOptions.phoneNumberId; }
            }

            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(data) });
            if (!response.ok) { throw (response); }
            const routinesData = (await response.json()).data;

            this.logger.debug(`[PersonalRoutinesService] updatePersonalRoutine -- SUCCESS -- `);

            return routinesData;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updatePersonalRoutine');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Creates a custom personal routine
     * @public
     * @param name - name of new personal routine
     * @returns resolves with the newly created personal routine data
     */
    public async createCustomPersonalRoutine(name: string): Promise<any> {
        try {
            if (!name) { throw new Error(`[PersonalRoutinesService] createCustomPersonalRoutine -- mandatory namle for custom routine`); }
            const url = this.portalURL;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ name: name }) });
            if (!response.ok) { throw (response); }
            const personalRoutineData = (await response.json()).data;
            this.logger.debug(`[PersonalRoutinesService] updatePersonalRoutine -- SUCCESS -- `);

            return personalRoutineData;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createCustomPersonalRoutine');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Delete a custom personal routine
     * @public
     * @param routineId - unique identifier of routine to be deleted
     * @returns resolves when routine is deleted
     */
    public async deleteCustomPersonalRoutine(routineId: string): Promise<void> {
        try {
            if (!routineId) { throw new Error(`[PersonalRoutinesService] deleteCustomPersonalRoutine -- no routineID for delete `); }
            const url = `${this.portalURL}/${routineId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteCustomPersonalRoutine');
            this.logger.error(`[PersonalRoutinesService] ${errorInfo.message}`);
            throw errorInfo.error;
        }

    }
}
